/**
 * `/api/auth/*`. No token here: the credential is an `HttpOnly` cookie the browser
 * attaches to every same-origin request, including the socket handshake.
 */

import { request } from './http';
import type { PlayedGame } from './results';

/** What the server will tell a client about itself. Never a hash, never a token. */
export interface AccountSummary {
  id: string;
  email: string | null;
  displayName: string;
  /** Shows the Site admin link; the server gates the page itself. */
  isAdmin?: boolean;
}

export const authApi = {
  /** Lets a first-run box offer "create your account" instead of an unpassable login. */
  status: () => request<{ hasAccount: boolean; registrationOpen: boolean }>('/auth/status'),

  /** Null when nobody is signed in, rather than a 401, so a first visit logs no error. */
  me: () => request<{ account: AccountSummary | null }>('/auth/me'),

  register: (email: string, password: string, displayName?: string) =>
    request<{ account: AccountSummary }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, displayName }),
    }),

  login: (email: string, password: string) =>
    request<{ account: AccountSummary }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  logout: () => request<void>('/auth/logout', { method: 'POST' }),

  /** Rename yourself. The email is the login identity and stays put. */
  updateDisplayName: (displayName: string) =>
    request<{ account: AccountSummary }>('/auth/me', {
      method: 'PATCH',
      body: JSON.stringify({ displayName }),
    }),

  /** Put seats this device played onto the account, each proven by its resume token. */
  linkSeats: (seats: { code: string; token: string }[]) =>
    request<{ linked: number }>('/auth/me/seats', { method: 'POST', body: JSON.stringify({ seats }) }),

  /** The account's finished games, newest first, on any device. */
  results: () => request<{ results: PlayedGame[] }>('/auth/me/results'),

  /** Current password required; 204 on success, 401 if the current one is wrong. */
  changePassword: (currentPassword: string, newPassword: string) =>
    request<void>('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    }),
};

/**
 * A copy of the server's floor (`auth/password.ts`), used only to encourage, never to
 * permit: the button stays enabled and the server decides.
 */
export const MIN_PASSWORD_LENGTH = 8;
